import React, { type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { Loader, Text, Box } from '@vibe/core'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import type { PermissionResource, PermissionAction } from '../../types'

interface PermissionGuardProps {
  children: ReactNode
  resource?: PermissionResource
  action?: PermissionAction
  requirePermission?: () => boolean
  fallbackPath?: string
  showFallback?: boolean
}

/**
 * PermissionGuard component for protecting routes and components based on user permissions
 * - Checks if user has specific permissions before rendering children
 * - Can redirect to fallback path or show fallback UI
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  resource,
  action,
  requirePermission,
  fallbackPath = '/dashboard',
  showFallback = false,
}) => {
  const { isAuthenticated, isLoading } = useAuth()
  const permissions = usePermissions()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader size="large" />
      </div>
    )
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Check permissions
  let hasPermission = true

  if (requirePermission) {
    hasPermission = requirePermission()
  } else if (resource && action && permissions) {
    hasPermission = permissions.hasPermission(resource, action)
  }

  // Handle permission denied
  if (!hasPermission) {
    if (showFallback) {
      return (
        <Box className="p-8 text-center">
          <Text color="secondary" className="mb-4">
            No tienes permisos para acceder a esta sección.
          </Text>
          <Text type="text3" color="secondary">
            Contacta al administrador si necesitas acceso.
          </Text>
        </Box>
      )
    }

    return <Navigate to={fallbackPath} replace />
  }

  return <>{children}</>
}

/**
 * Hook for conditional rendering based on permissions
 */
export const usePermissionGate = () => {
  const permissions = usePermissions()

  const canRender = (
    resource?: PermissionResource,
    action?: PermissionAction,
    customCheck?: () => boolean
  ): boolean => {
    if (customCheck) {
      return customCheck()
    }

    if (resource && action && permissions) {
      return permissions.hasPermission(resource, action)
    }

    return true
  }

  return { canRender }
}
